/**
 * **Les flèches de tendance des jauges** — SPEC-UI §3.1 : *« comparaison avec la valeur d'il y
 * a 24 h simulées ; ▲ si +2 ou plus, ▼ si −2 ou moins, ▬ sinon »*.
 *
 * ## D'où vient la valeur d'il y a 24 h
 *
 * La spec la voulait « fournie par l'API dans `gauges.trend24h` — à confirmer dans SPEC-API ».
 * Elle n'y a jamais été confirmée, et **elle n'a rien à y faire** : une flèche de tendance
 * n'est pas un fait de simulation, c'est une observation de l'écran. La calculer côté serveur
 * obligerait à porter un historique de jauges dans le `GameState` — donc à faire grossir un
 * état qui est cloné à chaque action et sérialisé en base ⟦A38⟧, pour une décoration. Pire :
 * ce serait un champ d'état de plus, et le premier arbitrage de ce projet est qu'un champ
 * d'état se paie.
 *
 * C'est donc de la **mémoire d'interface**, exactement comme le journal affiché et les étages
 * déjà annoncés (`serverGameClient.ts`) : un `localStorage` par run, purgé au-delà de 48 h
 * simulées, qui ne survit pas à un autre navigateur et n'a pas besoin de le faire.
 *
 * ## Ce qu'elle refuse de dire
 *
 * 🔑 **Pas d'échantillon assez vieux ⇒ pas de flèche du tout** (`null`, pas `"stable"`). C'est
 * la seule nuance qui compte ici : « ta santé mentale n'a pas bougé depuis hier » et « je ne
 * sais pas ce qu'elle valait hier » sont deux phrases différentes, et un ▬ affiché pendant les
 * 24 premières heures d'une run dirait la première en pensant la seconde. Une chaîne qui vient
 * de naître n'a pas de veille.
 *
 * Même raison pour la borne du seuil : elle est **paramétrable** parce que ±2 ne veut dire la
 * même chose que sur une jauge 0-100. On ne met pas de flèche sur le Momentum (0,8-1,5) ni sur
 * les Clicks avec le seuil des pourcentages — la maquette de §3.1 ne les en dote pas non plus.
 */

import { useEffect, useState } from "react";

import { useRunStore } from "../stores/runStore";

export type Tendance = "hausse" | "baisse" | "stable" | null;

/** La fenêtre de comparaison, en heures ÉVEILLÉES — l'horloge du moteur (§2.1). */
export const FENETRE_H = 24;
/** Au-delà, on jette : l'historique ne doit jamais croître avec la durée de la run. */
const GARDE_H = 48;
/** Le seuil de §3.1, pour une jauge 0-100. */
export const SEUIL_DEFAUT = 2;
/** Au plus trois runs mémorisées : un bac à sable et un compte, plus une de marge. */
const RUNS_GARDEES = 3;

const CLE_STOCKAGE = "maxime.tendances";

interface Echantillon {
  /** `clock_h` au moment du relevé. */
  h: number;
  v: number;
}

type Memoire = Record<string, Record<string, Echantillon[]>>;

function lire(): Memoire {
  try {
    const brut = localStorage.getItem(CLE_STOCKAGE);
    if (!brut) return {};
    const v: unknown = JSON.parse(brut);
    return v !== null && typeof v === "object" ? (v as Memoire) : {};
  } catch {
    // Quota, navigation privée, JSON abîmé : on perd des flèches, jamais une partie.
    return {};
  }
}

function ecrire(m: Memoire): void {
  try {
    localStorage.setItem(CLE_STOCKAGE, JSON.stringify(m));
  } catch {
    /* voir `lire` : c'est une décoration, elle n'a pas le droit de faire échouer un rendu */
  }
}

/**
 * Enregistre la valeur du moment et rend la tendance sur 24 h simulées.
 *
 * **Fonction pure de la mémoire** (elle rend la mémoire suivante), pour que le test comme le
 * hook aient la même vérité : le hook ne fait qu'y brancher React et `localStorage`.
 */
export function noter(
  memoire: Memoire,
  runId: string,
  cle: string,
  clockH: number,
  valeur: number,
  seuil: number,
): { memoire: Memoire; tendance: Tendance } {
  const parRun = memoire[runId] ?? {};
  const serie = parRun[cle] ?? [];

  // Un relevé par heure simulée au plus : deux actions dans la même heure ne créent qu'une
  // entrée, et c'est la dernière valeur qui fait foi.
  const dernier = serie[serie.length - 1];
  const suite =
    dernier && dernier.h === clockH
      ? [...serie.slice(0, -1), { h: clockH, v: valeur }]
      : [...serie, { h: clockH, v: valeur }];

  // On garde ce qui sert (la fenêtre) plus une marge, et rien d'autre.
  const purgee = suite.filter((e) => e.h >= clockH - GARDE_H);

  // Le plus RÉCENT relevé qui a au moins 24 h : c'est « la valeur d'il y a 24 h », pas la plus
  // vieille qu'on ait sous la main — sinon la flèche comparerait avec avant-hier.
  let ancien: Echantillon | undefined;
  for (const e of purgee) {
    if (e.h <= clockH - FENETRE_H) ancien = e;
    else break;
  }

  const suivante: Memoire = { ...memoire, [runId]: { ...parRun, [cle]: purgee } };

  if (!ancien) return { memoire: suivante, tendance: null };
  const delta = valeur - ancien.v;
  return {
    memoire: suivante,
    tendance: delta >= seuil ? "hausse" : delta <= -seuil ? "baisse" : "stable",
  };
}

/** Ne garde que les dernières runs vues — l'entrée de tête est celle qu'on vient d'écrire. */
function borner(m: Memoire, runId: string): Memoire {
  const cles = [runId, ...Object.keys(m).filter((k) => k !== runId)].slice(0, RUNS_GARDEES);
  const sortie: Memoire = {};
  for (const k of cles) if (m[k]) sortie[k] = m[k];
  return sortie;
}

/**
 * Le hook. `cle` absente ⇒ aucun suivi, aucune écriture, `null` — c'est l'état neutre : une
 * jauge qui ne demande pas de flèche se comporte exactement comme avant ⟦A-10⟧.
 *
 * ⚠️ Appelé **inconditionnellement** par `Jauge` (règle des hooks) : c'est le paramètre qui est
 * optionnel, jamais l'appel.
 */
export function useTendance24h(cle: string | undefined, valeur: number, seuil = SEUIL_DEFAUT): Tendance {
  const runId = useRunStore((s) => s.run?.runId);
  const clockH = useRunStore((s) => s.run?.state.clock_h);
  const [tendance, setTendance] = useState<Tendance>(null);

  useEffect(() => {
    if (cle === undefined || runId === undefined || clockH === undefined) {
      setTendance(null);
      return;
    }
    const resultat = noter(lire(), runId, cle, clockH, valeur, seuil);
    ecrire(borner(resultat.memoire, runId));
    setTendance(resultat.tendance);
  }, [cle, runId, clockH, valeur, seuil]);

  return tendance;
}

/** Le glyphe de §3.1. `null` ⇒ rien du tout, pas un tiret. */
export const GLYPHE: Record<"hausse" | "baisse" | "stable", string> = {
  hausse: "▲",
  baisse: "▼",
  stable: "▬",
};

export const DIT: Record<"hausse" | "baisse" | "stable", string> = {
  hausse: "en hausse depuis hier",
  baisse: "en baisse depuis hier",
  stable: "stable depuis hier",
};
